"use client";

import React, { useEffect } from "react";
import { Lock, ShieldCheck, CircleCheck, Shield, Loader, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
function ThreeDPlaceholder({
  icon: Icon,
  size = "size-5",
  className = ""
}: {
  icon: any;
  size?: string;
  className?: string;
}) {
  return <div className={`relative flex items-center justify-center ${className}`}>
            <div className={`absolute inset-0 bg-primary/20 rounded-full animate-pulse blur-[2px] ${size}`} />
            <Icon className={`${size} text-primary relative z-10`} />
        </div>;
}
function AcquisitionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      try {
        const pixelUrl = 'https://www.pixelhere.com/et/event.php?advertiser=330914&cid=443388&id=372de3';
        new Image().src = pixelUrl;
      } catch (error) {
        console.error("Tracking pixel failed", error);
      }
    }
  }, [isSubmitted, submitError]);
  if (isSubmitted && !submitError) {
    return <div data-section-id="111836" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-2">
                    <ThreeDPlaceholder icon={CircleCheck} size="size-8" />
                </div>
                <h3 className="text-2xl font-bold text-foreground font-sans tracking-tight uppercase">Case File Initialized</h3>
                <p className="text-muted-foreground max-w-sm mx-auto font-sans">{successMessage}</p>
            </div>;
  }
  const inputClasses = "h-10 bg-background/50 border-border focus:border-primary/50 rounded-sm font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="full_name" rules={{
        required: "Full Legal Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Full Legal Name</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="e.g., Alexander Sterling" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-mono">Verification of identity is required for recovery.</FormDescription>
                            <FormMessage className="text-xs font-mono" />
                        </FormItem>} />

                <FormField control={control} name="incident_type" rules={{
        required: "Incident Type is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Incident Type</FormLabel>
                            <FormControl>
                                <Input {...field} placeholder="e.g., Wallet Access Lost or Phishing Scam" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-mono">Specify the nature of the crypto asset loss.</FormDescription>
                            <FormMessage className="text-xs font-mono" />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="email" rules={{
        required: "Secure Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Secure Email Address</FormLabel>
                            <FormControl>
                                <Input {...field} type="email" placeholder="forensics@londonblockchain.com" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-mono">All communications are end-to-end encrypted.</FormDescription>
                            <FormMessage className="text-xs font-mono" />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Contact Phone is required",
        pattern: {
          value: /^\+?[0-9\s\-]{7,20}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Encrypted Contact Number</FormLabel>
                            <FormControl>
                                <Input {...field} type="tel" placeholder="+44 20 7946 0000" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-mono">Include international country code.</FormDescription>
                            <FormMessage className="text-xs font-mono" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="lost_amount" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Lost Amount</FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="e.g., 5 BTC or $10,000" className={inputClasses} />
                        </FormControl>
                        <FormDescription className="text-[10px] font-mono">Estimate of the assets currently inaccessible or stolen.</FormDescription>
                        <FormMessage className="text-xs font-mono" />
                    </FormItem>} />

            <FormField control={control} name="incident_description" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">Incident Description</FormLabel>
                        <FormControl>
                            <Textarea {...field} placeholder="Describe the type of assets, wallet addresses involved, and the nature of the loss..." className="min-h-[100px] bg-background/50 border-border focus:border-primary/50 rounded-sm font-sans" />
                        </FormControl>
                        <FormDescription className="text-[10px] font-mono">Provide as much technical detail as possible for asset tracing.</FormDescription>
                        <FormMessage className="text-xs font-mono" />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3">
                    <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive-foreground font-mono">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold shadow-[0_0_20px_hsla(180,100%,50%,0.2)] bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-sm uppercase tracking-wider" size="lg">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Encrypting Data...
                    </> : <>
                        <ThreeDPlaceholder icon={Lock} size="size-5" className="mr-2" />
                        Request a Confidential Consultation
                    </>}
            </Button>
            <div className="text-[10px] text-center text-muted-foreground mt-4 flex items-center justify-center gap-1.5 font-mono uppercase tracking-widest">
                <ThreeDPlaceholder icon={ShieldCheck} size="size-3.5" />
                Privacy Guaranteed. Secured by AES-256 Encryption.
            </div>
        </Form>;
}
export default function SecureAcquisitionPortal() {
  const certifications = ["Encrypted Blockchain Forensic Protocols", "Non-Custodial Recovery Procedures", "Global Asset Tracing Network", "Strict Confidentiality Agreements"];
  return <section id="case-submission-form" className="w-full py-16 md:py-24 lg:py-32 bg-background text-foreground overflow-hidden dark">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
                    
                    <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24">
                        <div className="space-y-4">
                            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-3 py-1 text-xs font-mono uppercase tracking-widest">
                                Secure Forensic Intake
                            </Badge>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter font-sans text-foreground leading-tight uppercase">
                                Submit Your <span className="text-primary">Recovery Case</span>
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-sans">
                                Our forensic experts specialize in tracing illicit transactions and recovering lost digital assets. Provide the details of your case below to begin the confidential evaluation process.
                            </p>
                        </div>

                        <div className="space-y-6 pt-4 border-t border-border/50">
                            <h3 className="text-xl font-bold flex items-center gap-2 text-foreground font-sans uppercase tracking-tight">
                                <ShieldCheck className="text-primary size-6" />
                                Forensic Standards
                            </h3>
                            <ul className="space-y-4">
                                {certifications.map((cert, i) => <li key={i} data-index={i} className="flex items-start gap-3 group">
                                        <div className="mt-0.5 bg-primary/10 rounded-full p-1 group-hover:bg-primary/20 transition-colors">
                                            <CircleCheck className="size-4 text-primary shrink-0" />
                                        </div>
                                        <span className="text-muted-foreground font-medium font-sans">{cert}</span>
                                    </li>)}
                            </ul>
                        </div>
                    </div>

                    <div className="lg:col-span-7 relative">
                        <div className="absolute -inset-4 bg-gradient-to-tr from-primary/10 to-transparent rounded-3xl blur-3xl -z-10" aria-hidden="true" />
                        
                        <div className="relative bg-card/40 backdrop-blur-xl rounded-sm border border-primary/20 shadow-[0_0_40px_rgba(0,0,0,0.5)] p-6 md:p-8 lg:p-10 overflow-hidden">
                            {}
                            <div className="absolute inset-0 pointer-events-none -z-10 bg-[radial-gradient(circle_at_50%_50%,rgba(0,255,255,0.05),transparent_70%)]" aria-hidden="true">
                                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
                            </div>

                            <div className="absolute top-6 right-6 hidden sm:block">
                                <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/30 flex items-center gap-1.5 py-1 px-3 shadow-sm font-mono text-[10px] uppercase tracking-tighter">
                                    <ThreeDPlaceholder icon={Shield} size="size-3.5" />
                                    Security Verified
                                </Badge>
                            </div>

                            <div className="mb-8 sm:pr-36">
                                <h3 className="text-2xl font-bold text-card-foreground font-sans uppercase tracking-tight">Case Submission Form</h3>
                                <p className="text-sm text-muted-foreground mt-2 font-sans">Initialize your recovery request. Our system utilizes secure nodes to process all sensitive data submissions.</p>
                            </div>

                            <FormProvider formKey="case_submission_form" formId="case_submission_form" sectionName="Secure Acquisition Portal" submissionEmail="contact@thelondonblockchaincentre.com" submissionUrl="/api/contact" defaultValues={{
              full_name: "",
              incident_type: "",
              email: "",
              phone: "",
              incident_description: "",
              lost_amount: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              incident_type: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "^\\+?[0-9\\s\\-]{7,20}$"
              },
              incident_description: {
                required: false
              },
              lost_amount: {
                required: false
              }
            }} submitText="Request a Confidential Consultation" successMessage="Your case file has been securely initialized. A senior forensic analyst will contact you via your encrypted email address within 24 hours.">
                                <AcquisitionForm />
                            </FormProvider>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}